<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { T } from '@tolgee/vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { computed } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useAd } from '@/composables/game/ad'
import ThePremium from '@/components/ThePremium.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { ExtraSpinCounts } from '@/types/fortune-wheel'

defineProps<{
  show: boolean,
  spinUpgrades: ExtraSpinCounts
}>()

const emit = defineEmits<{
  (e: 'hide'): void
  (e: 'buySpins'): void
  (e: 'onAdWatched'): void
}>()

const { player } = usePlayer()
const { showAd, watchedDailyAdsCount } = useAd()
const missingUpgradesCount = computed(() => 10 - ((player.value.upgradeCount ?? 0) % 10))

async function watchAd() {
  const watched = await showAd()
  if (watched) {
    emit('hide')
    emit('onAdWatched')
  }
}

</script>
<template>
  <TheBottomSheet
    :show="show"
    content-class="pt-8"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col gap-y-3">
      <div class="w-full flex flex-col gap-y-6">
        <h2 class="text-solid-white text-lg -tracking-[0.36px] font-bold">
          <T
            key-name="fortune_wheel.info.more_spins.title"
            default-value="How to get more spins?"
          />
        </h2>
        <div class="flex gap-x-2">
          <img
            src="../../assets/images/emojis/emoji-girls.png"
            class="size-6"
          >
          <p class="text-base max-[380px]:text-sm font-semibold text-white/60">
            <span
              class="pb-[1px] border-b-[0.5px] border-white/60"
              @click="router.push(RouteName.FRIENDS)"
            > <T
              key-name="fortune_wheel.info.more_spins.invite_friend"
              default-value="Invite a friend"
            /></span> {{ " " }}
            <T
              key-name="fortune_wheel.info.more_spins.and_get"
              default-value="and get"
            />
            {{ " " }} <span class="text-green-primary text-nowrap">
              <T
                key-name="fortune_wheel.info.more_spins.count_spin"
                default-value="{count} spins"
                :params="{count: spinUpgrades.referral}"
              />
            </span>
          </p>
        </div>

        <div class="flex gap-x-2">
          <img
            src="../../assets/images/emojis/emoji-analytics.png"
            class="size-6"
          >
          <p
            class="text-base max-[380px]:text-sm font-semibold text-white/60"
          >
            <span
              class="pb-[1px] border-b-[0.5px] border-white/60"
              @click="router.push(RouteName.MINE)"
            >
              <T
                v-if="missingUpgradesCount === 1"
                key-name="fortune_wheel.info.more_spins.upgrade_1_card"
                default-value="Buy 1 more card"
                :params="{count: 1}"
              /> <T
                v-else
                key-name="fortune_wheel.info.more_spins.upgrade_count_cards"
                default-value="Buy {count} more cards"
                :params="{count: missingUpgradesCount}"
              />
            </span><span>{{ ", " }}</span>
            <T
              key-name="fortune_wheel.info.more_spins.get"
              default-value="get"
            />
            {{ " " }} <span class="text-green-primary text-nowrap">
              <T
                key-name="fortune_wheel.info.more_spins.count_spin"
                default-value="{count, plural, one {# spin} other {# spins}}"
                :params="{count: spinUpgrades.upgrades}"
              />
            </span>
          </p>
        </div>
        <div class="flex gap-x-2">
          <img
            src="../../assets/images/emojis/emoji-arrow-up.png"
            class="size-6"
          >
          <p class="text-base max-[380px]:text-sm font-semibold text-white/60">
            <span
              class="pb-[1px] border-b-[0.5px] border-white/60"
              @click="router.push(RouteName.MINE)"
            > <T
              key-name="fortune_wheel.info.more_spins.level_up"
              default-value="Level up"
            /></span> {{ " " }}
            <T
              key-name="fortune_wheel.info.more_spins.and_get"
              default-value="and get"
            />
            {{ " " }} <span class="text-green-primary text-nowrap">
              <T
                key-name="fortune_wheel.info.more_spins.count_spin"
                default-value="{count, plural, one {# spin} other {# spins}}"
                :params="{count: spinUpgrades.levelUp}"
              />
            </span>
          </p>
        </div>
        <div
          class="flex gap-x-2"
        >
          <img
            src="@/assets/images/movie.png"
            class="size-6"
          >
          <p
            class="text-base max-[380px]:text-sm font-semibold text-white/60 text-wrap"
          >
            <span
              class="pb-[1px] border-b-[0.5px] border-white/60"
              :class="watchedDailyAdsCount >= spinUpgrades.ad.perDayCount && 'opacity-[67%] pointer-events-none'"
              @click="watchAd"
            > <T
              key-name="fortune_wheel.info.more_spins.watch_an_ad"
              default-value="Watch an ad"
            /></span><span>{{ ", " }}</span>
            <T
              key-name="fortune_wheel.info.more_spins.get"
              default-value="get"
            />
            {{ " " }} <span class="text-green-primary text-nowrap">
              <T
                key-name="fortune_wheel.info.more_spins.count_spin"
                default-value="{count, plural, one {# spin} other {# spins}}"
                :params="{count: spinUpgrades.ad.spinCount}"
              />
            </span>{{ " " }}
            <span class="text-xs text-medium text-white/[38%] text-nowrap">
              (<T
                v-if="!watchedDailyAdsCount"
                key-name="fortune_wheel.info.more_spins.count_per_day"
                default-value="{count} per day"
                :params="{count: spinUpgrades.ad.perDayCount}"
              />
              <T
                v-else
                key-name="fortune_wheel.info.more_spins.count_watched"
                default-value="{watchedCount}/{totalCount} watched"
                :params="{
                  watchedCount: watchedDailyAdsCount,
                  totalCount: spinUpgrades.ad.perDayCount
                }"
              />)
            </span>
          </p>
        </div>
      </div>
      <div class="text-center text-white/[87%] text-xs leading-6 font-bold uppercase tracking-[1px]">
        or
      </div>
      <BaseButton @click="$emit('buySpins')">
        <template #leftIcon>
          <ThePremium
            image-class="size-5 min-w-[20px]"
            :with-tooltip="false"
          />
        </template>
        <T
          key-name="fortune_wheel.buy_more_spins"
          default-value="Buy more spins"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>
